import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../services/permissionService';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { currencyFormat } from '../utils/constants';
import {
  Expense,
  addExpense,
  getAllExpenses,
  updateExpense,
  deleteExpense
} from '../services/expenseService';

const ExpensesPage: React.FC = () => {
  // الحصول على بيانات المستخدم الحالي
  const { currentUser } = useAuth();

  // State variables
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [filteredExpenses, setFilteredExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Expense totals
  const [totalBasicExpenses, setTotalBasicExpenses] = useState(0);
  const [totalDailyExpenses, setTotalDailyExpenses] = useState(0);

  // التحقق من صلاحيات المستخدم
  const canViewBasicExpenses = hasPermission(currentUser, 'canViewBasicExpenses');
  const canViewDailyExpenses = hasPermission(currentUser, 'canViewDailyExpenses');
  const canAddExpenses = hasPermission(currentUser, 'canAddExpenses');
  const canEditExpenses = hasPermission(currentUser, 'canEditExpenses');
  const canDeleteExpenses = hasPermission(currentUser, 'canDeleteExpenses');

  // Filter state
  const [activeTab, setActiveTab] = useState<'all' | 'basic' | 'daily'>('all');

  // Form data - تعيين تاريخ اليوم تلقائياً
  const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // تعيين الوقت إلى بداية اليوم
    return today;
  };

  const [formData, setFormData] = useState<Omit<Expense, 'id' | 'createdAt' | 'updatedAt'>>({
    type: 'daily',
    category: '',
    amount: 0,
    description: '',
    date: getTodayDate()
  });

  // حالة منفصلة لحقل المبلغ كنص لسهولة الإدخال
  const [amountInput, setAmountInput] = useState<string>('');

  // تحميل البيانات عند تحميل الصفحة
  useEffect(() => {
    loadExpenses();
  }, []);

  // تصفية المصروفات عند تغيير التاب النشط
  useEffect(() => {
    filterExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, expenses]);

  // تحميل جميع المصروفات
  const loadExpenses = async () => {
    try {
      setLoading(true);
      // جلب جميع المصروفات
      const allExpenses = await getAllExpenses();
      setExpenses(allExpenses);

      // حساب إجمالي المصروفات الأساسية واليومية يدوياً
      let basicExpensesTotal = 0;
      let dailyExpensesTotal = 0;

      // تصنيف المصروفات حسب النوع
      allExpenses.forEach(expense => {
        if (expense.type === 'basic') {
          basicExpensesTotal += expense.amount;
        } else if (expense.type === 'daily') {
          dailyExpensesTotal += expense.amount;
        }
      });

      console.log('Basic expenses total:', basicExpensesTotal);
      console.log('Daily expenses total:', dailyExpensesTotal);

      // تحديث الحالة
      setTotalBasicExpenses(basicExpensesTotal);
      setTotalDailyExpenses(dailyExpensesTotal);
    } catch (error) {
      console.error('Error loading expenses:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحميل المصاريف',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // تصفية المصروفات حسب النوع والصلاحيات
  const filterExpenses = () => {
    // فلترة المصروفات حسب الصلاحيات أولاً
    let permissionFilteredExpenses = expenses.filter(expense => {
      if (expense.type === 'basic' && !canViewBasicExpenses) return false;
      if (expense.type === 'daily' && !canViewDailyExpenses) return false;
      return true;
    });

    // ثم فلترة حسب التاب النشط
    if (activeTab === 'all') {
      setFilteredExpenses(permissionFilteredExpenses);
    } else {
      const filtered = permissionFilteredExpenses.filter(expense => expense.type === activeTab);
      setFilteredExpenses(filtered);
    }
  };

  const handleOpenDialog = (expense?: Expense) => {
    if (expense) {
      setSelectedExpense(expense);
      setFormData({
        type: expense.type,
        category: expense.category,
        amount: expense.amount,
        description: expense.description,
        date: expense.date
      });
      setAmountInput(expense.amount.toString());
    } else {
      setSelectedExpense(null);
      // تعيين تاريخ اليوم تلقائياً للمصروف الجديد
      const todayDate = getTodayDate();

      console.log('🗓️ تعيين تاريخ اليوم تلقائياً للمصروف الجديد:', todayDate.toISOString().split('T')[0]);

      setFormData({
        type: 'daily',
        category: '',
        amount: 0,
        description: '',
        date: todayDate
      });
      setAmountInput(''); // حقل فارغ للمصروف الجديد
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExpense(null);
    setAmountInput(''); // إعادة تعيين حقل المبلغ
  };

  const handleSave = async () => {
    try {
      // تحويل المبلغ من نص إلى رقم
      const amount = parseFloat(amountInput) || 0;
      const dataToSave = { ...formData, amount };

      if (selectedExpense) {
        await updateExpense(selectedExpense.id!, dataToSave);
        setSnackbar({
          open: true,
          message: 'تم تحديث المصروف بنجاح',
          severity: 'success'
        });
      } else {
        await addExpense(dataToSave);
        setSnackbar({
          open: true,
          message: 'تم إضافة المصروف بنجاح',
          severity: 'success'
        });
      }
      handleCloseDialog();
      loadExpenses();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'فشل في حفظ المصروف',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteExpense(id);
      setSnackbar({
        open: true,
        message: 'تم حذف المصروف بنجاح',
        severity: 'success'
      });
      loadExpenses();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'فشل في حذف المصروف',
        severity: 'error'
      });
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        المصاريف
      </Typography>

      {/* بطاقات المعلومات */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {canViewBasicExpenses && (
          <Grid item xs={12} md={canViewDailyExpenses ? 6 : 12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  المصاريف الأساسية (شهرية)
                </Typography>
                <Typography variant="h4" color="primary">
                  {currencyFormat(totalBasicExpenses)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  تخصم من المكسب الكلي آخر الشهر
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {canViewDailyExpenses && (
          <Grid item xs={12} md={canViewBasicExpenses ? 6 : 12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  المصاريف اليومية
                </Typography>
                <Typography variant="h4" color="secondary">
                  {currencyFormat(totalDailyExpenses)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  تخصم يومياً من مكسب اليوم
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* أزرار التصفية والإضافة */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="all" label="جميع المصروفات" />
          {canViewBasicExpenses && (
            <Tab value="basic" label="المصروفات الأساسية" />
          )}
          {canViewDailyExpenses && (
            <Tab value="daily" label="المصروفات اليومية" />
          )}
        </Tabs>

        {canAddExpenses && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            إضافة مصروف
          </Button>
        )}
      </Box>

      {/* جدول المصروفات */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>النوع</TableCell>
              <TableCell>التصنيف</TableCell>
              <TableCell>المبلغ</TableCell>
              <TableCell>التاريخ</TableCell>
              <TableCell>الوصف</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : filteredExpenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  لا توجد مصروفات
                </TableCell>
              </TableRow>
            ) : (
              filteredExpenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell>
                    <Chip
                      label={expense.type === 'basic' ? 'أساسي' : 'يومي'}
                      color={expense.type === 'basic' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{expense.category}</TableCell>
                  <TableCell>{currencyFormat(expense.amount)}</TableCell>
                  <TableCell>{expense.date.toLocaleDateString()}</TableCell>
                  <TableCell>{expense.description}</TableCell>
                  <TableCell>
                    {canEditExpenses && (
                      <IconButton color="primary" onClick={() => handleOpenDialog(expense)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    {canDeleteExpenses && (
                      <IconButton color="error" onClick={() => handleDelete(expense.id!)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                    {!canEditExpenses && !canDeleteExpenses && (
                      <Typography variant="caption" color="text.secondary">
                        لا توجد صلاحيات
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* نموذج إضافة/تعديل مصروف */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedExpense ? 'تعديل مصروف' : 'إضافة مصروف'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* نوع المصروف */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>نوع المصروف</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => {
                    const newType = e.target.value as 'basic' | 'daily';
                    // إذا تم اختيار مصروف يومي، تعيين تاريخ اليوم تلقائياً
                    if (newType === 'daily') {
                      const todayDate = getTodayDate();
                      setFormData({ ...formData, type: newType, date: todayDate });
                      console.log('🗓️ تم تعيين تاريخ اليوم تلقائياً للمصروف اليومي:', todayDate.toISOString().split('T')[0]);
                    } else {
                      setFormData({ ...formData, type: newType });
                    }
                  }}
                >
                  <MenuItem value="daily">
                    يومي - يخصم من مكسب اليوم
                  </MenuItem>
                  <MenuItem value="basic">
                    أساسي (شهري) - يخصم من المكسب الكلي آخر الشهر
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* تصنيف المصروف */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>التصنيف</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <MenuItem value="إيجار">إيجار</MenuItem>
                  <MenuItem value="كهرباء">كهرباء</MenuItem>
                  <MenuItem value="مياه">مياه</MenuItem>
                  <MenuItem value="رواتب">رواتب</MenuItem>
                  <MenuItem value="مواصلات">مواصلات</MenuItem>
                  <MenuItem value="طعام">طعام</MenuItem>
                  <MenuItem value="مستلزمات">مستلزمات</MenuItem>
                  <MenuItem value="أخرى">أخرى</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* المبلغ */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="المبلغ"
                type="number"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                placeholder="أدخل المبلغ"
                InputProps={{
                  startAdornment: <span style={{ marginRight: '8px' }}>EGP</span>,
                }}
              />
            </Grid>

            {/* التاريخ */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="التاريخ"
                type="date"
                value={formData.date.toISOString().split('T')[0]}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  selectedDate.setHours(0, 0, 0, 0); // تعيين الوقت إلى بداية اليوم
                  setFormData({ ...formData, date: selectedDate });
                  console.log('📅 تم تغيير التاريخ إلى:', selectedDate.toISOString().split('T')[0]);
                }}
                InputLabelProps={{ shrink: true }}
                helperText={
                  formData.type === 'daily'
                    ? "💡 المصاريف اليومية تخصم من مكسب اليوم المحدد"
                    : "💡 المصاريف الأساسية تخصم من المكسب الشهري"
                }
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={() => {
                        const todayDate = getTodayDate();
                        setFormData({ ...formData, date: todayDate });
                        console.log('🗓️ تم تعيين تاريخ اليوم:', todayDate.toISOString().split('T')[0]);
                      }}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      اليوم
                    </Button>
                  )
                }}
              />
            </Grid>

            {/* الوصف */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>إلغاء</Button>
          <Button onClick={handleSave} variant="contained" color="primary">حفظ</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpensesPage;